import { typeid } from 'typeid-js';
import { LiveBroadcast, YouTubeLiveStream } from '../types/youtube';
import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { z } from 'zod';
import {
  Destination,
  DestinationMetadataApiClient,
  DestinationProviderApiClient,
  YouTubeOutputAuxiliaryData,
} from './DestinationsApiClient';
import { UnauthenticatedError } from '../utils/auth';
import { promisefy } from '../utils/promisefy';
import { AddOutputInfo } from '../types/obs';
import { queryOptions } from '@tanstack/react-query';

const youtubeKnownErrors = z.object({
  error: z.object({
    code: z.number(),
    message: z.string(),
    errors: z.array(
      z.object({
        message: z.string(),
        reason: z.enum(['liveStreamingNotEnabled']),
        extendedHelp: z.string(),
      })
    ),
  }),
});

export class YoutubeApiClient implements DestinationProviderApiClient<'youtube'> {
  static STREAM_SOURCE_NAME = 'SE.Live Streaming Source';
  private static readonly CLIENT_ID = '722996816243-m2seibvttl7lc2g4qhajjdlrikjlv4uj.apps.googleusercontent.com';
  private static readonly SCOPES = [
    'https://www.googleapis.com/auth/youtube.force-ssl',
    'https://www.googleapis.com/auth/youtube.readonly',
    'https://www.googleapis.com/auth/youtube',
  ];

  private destinationMetadata: Destination<'youtube'>;
  private authContextProvider: AuthContextProvider;
  private serviceEnvironment: ServiceEnvironment;
  constructor({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata,
  }: {
    authContextProvider: AuthContextProvider;
    destinationMetadata: Destination<'youtube'>;
    serviceEnvironment: ServiceEnvironment;
  }) {
    this.authContextProvider = authContextProvider;
    this.destinationMetadata = destinationMetadata;
    this.serviceEnvironment = serviceEnvironment;
  }

  public youtubeBroadcastListQueryOptions(accessToken: string) {
    return queryOptions({
      queryKey: ['youtube-broadcasts', accessToken],
      queryFn: () => this.fetchBroadcasts({ status: 'upcoming' }),
    });
  }

  public static getAuthorizationUrl(returnUrl: string): string {
    const state = typeid().toString();
    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    url.searchParams.set('client_id', YoutubeApiClient.CLIENT_ID);
    url.searchParams.set('redirect_uri', returnUrl);
    url.searchParams.set('response_type', 'token');
    url.searchParams.set('scope', YoutubeApiClient.SCOPES.join(' '));
    url.searchParams.set('state', state);
    url.searchParams.set('include_granted_scopes', 'true');
    return url.toString();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, string | number | boolean> } = {}
  ): Promise<T> {
    const url = new URL(`https://www.googleapis.com/youtube/v3/${endpoint}`);
    url.searchParams.set('key', YoutubeApiClient.CLIENT_ID);

    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        url.searchParams.set(key, value?.toString());
      });
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${this.destinationMetadata.accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      const parsedError = youtubeKnownErrors.safeParse(error);

      if (parsedError.success) {
        throw new Error(parsedError.data.error.errors[0].message);
      }

      if (response.status === 401) {
        throw new UnauthenticatedError('Unauthenticated');
      }

      throw new Error(`YouTube API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  public async fetchBroadcasts({
    status,
    broadcastId,
  }: {
    status?: 'upcoming' | 'active' | 'completed' | 'all',
    broadcastId?: string,
  }): Promise<LiveBroadcast[]> {
    const searchParams = new URLSearchParams();

    if (status) {
      searchParams.set('broadcastStatus', status);
    }

    if (broadcastId) {
      searchParams.set('id', broadcastId);
    }

    searchParams.set('part', 'id,snippet,contentDetails,monetizationDetails,status');

    const response = await this.makeRequest<{ items: LiveBroadcast[] }>(`liveBroadcasts?${searchParams.toString()}`);

    return response.items;
  }

  public async fetchLiveStream({ liveStreamId }: { liveStreamId?: string }) {
    if (!liveStreamId) {
      return null;
    }

    const response = await this.makeRequest<{
      etag: string;
      items: [YouTubeLiveStream]; // One item array
    }>('liveStreams', {
      params: {
        part: 'id,snippet,cdn,contentDetails,status',
        id: liveStreamId,
        maxResults: 1,
      },
    }).catch(() => null);

    return response?.items?.[0] ?? null;
  }

  public async fetchStreamKeys() {
    const response = await this.makeRequest<{
      items: YouTubeLiveStream[];
    }>('liveStreams', {
      params: {
        part: 'id,cdn,snippet',
        mine: true,
        maxResults: 50,
      },
    });

    return response.items;
  }

  public async removeStreams() {
    const streams = await this.fetchStreamKeys();

    for (const stream of streams) {
      try {
        await this.makeRequest('liveStreams', {
          method: 'DELETE',
          params: { id: stream.id },
        });
      } catch (error) {
        console.error('Failed to delete stream', stream.id, error);
      }
    }
  }

  public async createBroadcast({
    title = YoutubeApiClient.STREAM_SOURCE_NAME,
    categoryId,
    privacyStatus = 'public',
    scheduledStartTime = new Date(Date.now() + 60 * 1000),
  }: {
    title?: string;
    categoryId: string;
    privacyStatus?: string;
    scheduledStartTime?: Date;
  }) {
    return this.makeRequest<LiveBroadcast>('liveBroadcasts?part=snippet,status,contentDetails', {
      method: 'POST',
      body: JSON.stringify({
        snippet: {
          title,
          scheduledStartTime: scheduledStartTime.toISOString(),
          categoryId,
        },
        status: {
          privacyStatus,
          selfDeclaredMadeForKids: false,
        },
        contentDetails: {
          enableAutoStart: true,
          enableAutoStop: true,
          monitorStream: {
            enableMonitorStream: true,
            broadcastStreamDelayMs: 0,
          },
        },
      }),
    });
  }

  public async createStream({ title, categoryId }: { title: string; categoryId: string }) {
    return this.makeRequest<YouTubeLiveStream>('liveStreams?part=snippet,cdn', {
      method: 'POST',
      body: JSON.stringify({
        snippet: {
          title,
          categoryId,
        },
        cdn: {
          frameRate: 'variable',
          ingestionType: 'rtmp',
          resolution: 'variable',
        },
      }),
    });
  }

  public async bindBroadcastToStream(broadcastId: string, streamId: string) {
    return this.makeRequest<{
      cdn: {
        ingestionInfo: {
          ingestionAddress: string;
          streamName: string;
        };
      };
    }>(`liveBroadcasts/bind?id=${broadcastId}&part=id&streamId=${streamId}`, {
      method: 'POST',
    });
  }

  // private async findBroadcastWithinTimeRange({
  //   broadcasts,
  //   range,
  //   outputId,
  //   streamTitle,
  // }: {
  //   broadcasts: LiveBroadcast[];
  //   range: { start?: Date; end: Date };
  //   outputId: string;
  //   streamTitle: string;
  // }): Promise<string | undefined> {
  //   const { start = new Date(), end } = range;

  //   let broadcastId: string | undefined;

  //   // TODO: Allow the user to pick a broadcast from the list of scheduled broadcasts.
  //   if (broadcasts.length > 1) {
  //     broadcastId = await navigateInModal<string>({
  //       to: '/select-output/youtube',
  //       title: 'Select a broadcast',
  //       search: {
  //         destination_id: this.destinationMetadata.id,
  //         output_id: outputId,
  //         streamTitle: encodeURIComponent(streamTitle),
  //       },
  //       height: 700,
  //     });

  //     if (!broadcastId) {
  //       throw new Error('User cancelled broadcast selection');
  //     }

  //     return broadcastId;
  //   }

  //   return broadcasts.find((broadcast) => {
  //     const isTitleMatch = broadcast.snippet.title === YoutubeApiClient.STREAM_SOURCE_NAME;

  //     if (isTitleMatch) {
  //       return true;
  //     }

  //     const isDateMatch =
  //       new Date(broadcast.snippet.scheduledStartTime) >= start &&
  //       new Date(broadcast.snippet.scheduledStartTime) <= end;

  //     if (!isDateMatch) {
  //       return false;
  //     }

  //     const isPrivacyStatusMatch = broadcast.status.privacyStatus === 'public';

  //     return isPrivacyStatusMatch;
  //   })?.id;
  // }

  public async getStreamingOutputInfo({
    title,
    categoryId,
    liveStreamId = 'new',
    // outputId,
    broadcastId: broadcastIdParam = 'new',
  }: {
    title: string;
    categoryId: string;
    liveStreamId?: string;
    outputId: string;
    broadcastId?: string;
  }) {
    let broadcastId = broadcastIdParam;

    console.log('getStreamingOutputInfo', {
      title,
      categoryId,
      liveStreamId,
      broadcastId,
    });

    if (!broadcastId || broadcastId === 'new') {
      const { id } = await this.createBroadcast({ title: title || YoutubeApiClient.STREAM_SOURCE_NAME, categoryId });
      broadcastId = id;
    }

    // Try to find an existing stream
    let stream = liveStreamId && liveStreamId !== 'new' ? await this.fetchLiveStream({ liveStreamId }) : null;

    if (!stream) {
      stream = await this.createStream({
        categoryId,
        title,
      });
    }

    if (!stream) {
      throw new Error('Failed to create or fetch stream');
    }

    console.log('existingStream', {
      liveStreamId,
      stream,
    });

    // if (!stream) {
    //   // Create a new stream only if no existing stream is found
    //   stream = await this.createStream({ title, categoryId });
    // } else {
    //   console.log('Reusing existing stream key');
    // }

    await this.bindBroadcastToStream(broadcastId, stream.id);

    return {
      broadcastId,
      liveStreamId: stream.id,
      streamKey: stream.cdn.ingestionInfo.streamName,
      serverUrl: stream.cdn.ingestionInfo.ingestionAddress,
    };
  }

  async setupStreamRoom({
    destinationMetadata,
    ...streamSetup
  }: {
    title: string;
    destinationMetadata: Destination;
    categoryId: string;
    outputId: string;
    liveStreamId?: string;
  }) {
    const destinationMetadataApiClient = new DestinationMetadataApiClient({
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
      destinationMetadata,
    });

    const { outputs } = destinationMetadataApiClient.get();

    if (!outputs.length) return;

    const activeOutputs = await promisefy(window.host.getAllStreamingOutputs)();

    const outputsWithStreamingSettings = await Promise.all(
      (outputs as AddOutputInfo<YouTubeOutputAuxiliaryData>[]).map(async (output) => {
        if (output.id !== streamSetup.outputId) {
          console.log('Skipping output', output.id, 'as it is not the template output');
          return output;
        }

        if (activeOutputs[output.id]?.isActive) {
          console.log('Skipping output', output.id, 'as it is active');
          return output;
        }

        if (!output.isEnabled) {
          console.log('Skipping output', output.id, 'as it is not enabled');
          return output;
        }

        const { streamKey, serverUrl, broadcastId, liveStreamId } = await this.getStreamingOutputInfo(streamSetup);

        return {
          ...output,
          streamingSettings: { serverUrl, streamKey },
          auxiliaryData: {
            ...output.auxiliaryData,
            destinationId: destinationMetadata.id,
            isPrimaryOutput: output.auxiliaryData?.isPrimaryOutput,
            streamPrivacyStatus: output.auxiliaryData?.streamPrivacyStatus,
            streamTitle: streamSetup.title,
            streamCategory: streamSetup.categoryId,
            broadcastId,
            liveStreamId,
          },
        } as AddOutputInfo<YouTubeOutputAuxiliaryData>;
      })
    );

    return destinationMetadataApiClient.updateDestination({
      outputs: outputsWithStreamingSettings,
    });
  }

  public async updateStreamRoom(params: {
    title?: string;
    categoryId?: string;
    privacyStatus?: string;
    outputId: string;
  }) {
    const output = this.destinationMetadata.outputs.find((output) => output.id === params.outputId) as
      | AddOutputInfo<YouTubeOutputAuxiliaryData>
      | undefined;

    if (!output) {
      throw new Error('Output not found');
    }

    // Update video title and category
    await this.makeRequest('videos?part=snippet', {
      method: 'PUT',
      body: JSON.stringify({
        id: output.auxiliaryData.broadcastId,
        snippet: {
          title: params.title,
          categoryId: params.categoryId,
        },
      }),
    });
  }

  public async getCategories({ query }: { query: string }) {
    const result = await this.makeRequest<{
      items: {
        id: string;
        snippet: {
          title: string;
        };
      }[];
    }>('videoCategories', {
      params: {
        q: query,
        part: 'snippet',
        regionCode: 'US',
        maxResults: 50,
      },
    });

    return new Map(result.items.map((item) => [item.id, item.snippet.title]));
  }

  public async getCategoryById(params: { categoryId: string }): Promise<{ id: string; displayName: string } | null> {
    void params;
    return null;
  }

  public async endBroadcast({ broadcastId }: { broadcastId?: string }): Promise<void> {
    if (!broadcastId) {
      throw new Error('Broadcast ID is required');
    }

    await this.makeRequest(`liveBroadcasts/transition?id=${broadcastId}&broadcastStatus=complete&part=snippet`, {
      method: 'POST',
      params: {},
    });
  }
}
